import BasePageLayout from '../../components/layouts/base-page-layout/base-page-layout';
import OfferedPlansTitle from "./components/offered-plans-title/offered-plans-title";
import OfferedSubscriptionsPlansCarousel from "./components/offered-plans-carousel/offered-plans-carousel";
import FontHeading3 from "../../components/fonts/font-heading3/font-heading3";
import {useCallback, useEffect, useState} from "react";
import subscriptionPlansStore from "./stores/subscription-plans-store";
import SubscriptionPlansStore, {IPlanButton} from "./stores/subscription-plans-store";
import {FONT_COLOR, FONT_VARIANT} from "../../components/fonts/font-props";
import PlanInfo from "./components/plan-info/plan-info";
import {observer} from "mobx-react-lite";
import portalStore from "../../stores/current-portal-store";
import PlanInfoAddonsSection from "./components/plan-info-addons-section/plan-info-addons-section";
import PlanCard from "./components/plan-card/plan-card";
import './subscription-plans-page.scss';
import Ahref from "../../components/ahref/ahref";
import FontBody2 from "../../components/fonts/font-body2/font-body2";
import { AmazonMcfModal } from './components/amazon-mcf-modal/amazon-mcf-modal';
import { INTEGRATION_CODE } from '../../models/IntegrationsTypes';
import integrationsListStore from '../integrations/products-services/stores/integrations-list-store';
import { PageLoader } from '../../components/page-loader/page-loader';
import FontHeading2 from "../../components/fonts/font-heading2/font-heading2";
import { SwiperSlide } from 'swiper/react';
import ContactUsPlanInfo from './components/offered-plans-carousel/contact-us-plan-info/contact-us-plan-info';
import dashboardSettingsStore from "../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../models/ProfileCapabilities";

interface ISubscriptionPlansPage {
}


const SubscriptionPlansPage: React.FC<ISubscriptionPlansPage> = observer(() => {
    const subscriptionPlansCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.SUBSCRIPTION_PLANS);
    const currentPortal = portalStore.currentPortal;
    const [showAmazonMcfRegistrationModal, setShowAmazonMcfRegistrationModal] = useState(false);

    useEffect(() => {
        subscriptionPlansStore.load();
        portalStore.load();
        integrationsListStore.load();
    }, []);

    const currentPlan = subscriptionPlansStore.userPlan;
    const userAddons = currentPlan?.userAddons;

    const pageIsReady = !integrationsListStore.isLoading
                        && !subscriptionPlansStore.isLoading
                        && !portalStore.isLoading;

    const plans = SubscriptionPlansStore.planList.filter(p => (p.partnerId === currentPortal.partnerId));


    const pageTitle = () => {
        /** temp solution, we will not read query params inside components, should be service for this */
        const searchParams = new URLSearchParams(window.location?.search);
        const hideStoreName = !!searchParams.get('hideStoreName');

        if (!hideStoreName && portalStore.currentPortal) {
            return `Subscription Plans for ${portalStore.currentPortal.name ?? ''}`;
        }
        return 'Subscription Plans';
    }

    useEffect(() => {
        if (
            !currentPortal.planId
            && currentPortal.partnerId === INTEGRATION_CODE.AMAZON_MCF
            && pageIsReady
            && !!!integrationsListStore.getIntegrationByCode(INTEGRATION_CODE.AMAZON_MCF)
        ) {
            setShowAmazonMcfRegistrationModal(true);
        }
    }, [currentPortal.planId, currentPortal.partnerId, pageIsReady]);

    const closeAmazonMcfRegistrationModal = useCallback((isConnected: boolean) => {
        if (!isConnected) {
            currentPortal.setPartnerId(null);
            currentPortal.save();
        }
        setShowAmazonMcfRegistrationModal(false);
    }, [currentPortal]);

    /** Component level guard: return null if subscription plans are not supported */
    if (!subscriptionPlansCapability.isSupported) {
        return null;
    }

    return (
        <BasePageLayout title={pageTitle()}>
            {!pageIsReady && <PageLoader />}
            <div className="subscription-plans-page-wrapper">
                {   SubscriptionPlansStore.userPlan &&
                    <div className="subscription-plans-addons-notifier">
                        <FontBody2>To enrich your plan with add-ons, go to the
                            {'\u00A0'}
                            <Ahref href={'admin/accounts'}>
                                <FontBody2 color={FONT_COLOR.ACCENT1}>Store Account</FontBody2>
                            </Ahref>
                            {'\u00A0'}
                            page
                        </FontBody2>
                    </div>
                }
                <div className="subscriptions-plan-page-layout">
                    {!!currentPlan?.id &&
                        <div className="subscription-current-plan-layout">
                            <FontHeading3 color={FONT_COLOR.PRIMARY3} variant={FONT_VARIANT.BOLD}>Current
                                Plan
                            </FontHeading3>
                            <PlanCard labeled={false} labelBgColorVariable={'transparent'} labelText={''}
                                      planCardBgColor={'transparent'}>
                                <>
                                    {
                                        currentPlan && <PlanInfo plan={currentPlan} hideButton planButtonData={{} as IPlanButton}/>
                                    }
                                    {
                                        (userAddons && userAddons?.length !== 0)
                                        && userAddons && currentPlan
                                        && <PlanInfoAddonsSection isCurrentPlan={true} addons={userAddons}/>
                                    }
                                </>
                            </PlanCard>
                        </div>
                    }
                    <div className="offered-plans-layout">
                    {
                        !currentPortal.hasChildren &&
                        <>
                            <OfferedPlansTitle title={!!currentPlan?.id ? 'Available plans' : '14-day free trial with any plan'}/>
                            <OfferedSubscriptionsPlansCarousel showCarouselButtons={plans?.length > 2}>
                                {
                                    plans.map((plan, index) => {
                                        return <SwiperSlide key={index}>
                                            <PlanCard
                                                labeled={!!plan.tag}
                                                labelBgColorVariable={plan.tag_color}
                                                labelText={plan.tag}
                                            >
                                                <>
                                                    <PlanInfo
                                                        plan={plan}
                                                        planButtonData={SubscriptionPlansStore.getButtonData(plan)}
                                                    />
                                                    {
                                                        (SubscriptionPlansStore.userPlan?.userAddons && SubscriptionPlansStore.userPlan?.userAddons?.length !== 0) &&
                                                        <PlanInfoAddonsSection isCurrentPlan={false} addons={plan.userAddons}/>
                                                    }
                                                </>
                                            </PlanCard>
                                        </SwiperSlide>
                                    })}
                                {
                                    currentPlan && <SwiperSlide key={currentPlan.id}>
                                        <PlanCard labeled={false} labelBgColorVariable={'var(--color-neutral-7)'}>
                                            <ContactUsPlanInfo contactUsPlanTitle="Need help?" />
                                        </PlanCard>
                                    </SwiperSlide>
                                }
                            </OfferedSubscriptionsPlansCarousel>
                        </>

                    }
                    {
                        currentPortal.hasChildren &&
                        <div className="parent-user-carousel-layout">
                            <FontHeading2>
                                To change the current plan, please contact support.
                            </FontHeading2>
                        </div>
                    }
                    </div>

                </div>
            </div>

            { /** Commented out as a part of Removing Subscription Block for new IL merchants */ }
            {/* {portalStore.currentPortal?.isSubscriptionsBlocked &&
                <BlockIlStores />
            } */}
            
            {showAmazonMcfRegistrationModal && <AmazonMcfModal
                onClose={closeAmazonMcfRegistrationModal}
            />}
        </BasePageLayout>
    );
})

export default SubscriptionPlansPage;
